/**
 * Enhanced User Security Component
 *
 * Provides advanced security features including:
 * - User verification management
 * - Password reset requirements
 * - Role-based security controls
 * - Cohort assignments for facilitators/assessors
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Button,
  Alert,
  Badge,
  Card,
  Row,
  Col,
  Table,
  Accordion
} from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import roleManagementService, { ROLES } from '../services/roleManagementService';
import '../styles/brand.css';

const EnhancedUserSecurity = ({ user, show, onHide, onUpdate }) => {
  const { userProfile } = useAuth();

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Security settings
  const [isVerified, setIsVerified] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [requirePasswordReset, setRequirePasswordReset] = useState(false);
  const [passwordResetReason, setPasswordResetReason] = useState('');

  // Role management
  const [selectedRole, setSelectedRole] = useState('');
  const [roleChangeReason, setRoleChangeReason] = useState('');

  // Cohort assignments
  const [assignedCohorts, setAssignedCohorts] = useState([]);
  const [availableCohorts, setAvailableCohorts] = useState([]);

  useEffect(() => {
    if (user && show) {
      initializeUserData();
    }
  }, [user, show]);

  const initializeUserData = () => {
    setIsVerified(user.isVerified || false);
    setVerificationNotes(user.verificationNotes || '');
    setSelectedRole(user.role || 'learner');
    setAssignedCohorts(user.assignedCohorts || []);
    setRequirePasswordReset(user.requiresPasswordReset || false);

    // Load available cohorts (placeholder - would come from actual cohorts collection)
    setAvailableCohorts([
      { id: 'cohort1', name: 'Software Development Cohort 1' },
      { id: 'cohort2', name: 'Software Development Cohort 2' },
      { id: 'cohort3', name: 'Data Analytics Cohort 1' },
      { id: 'cohort4', name: 'Digital Marketing Cohort 1' }
    ]);
  };

  const handleVerificationUpdate = async () => {
    setLoading(true);
    setError('');

    try {
      await roleManagementService.updateUserVerification(
        user.id,
        isVerified,
        verificationNotes
      );

      setSuccess('Verification status updated successfully');
      onUpdate && onUpdate();
    } catch (error) {
      setError('Failed to update verification: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetRequirement = async () => {
    if (!passwordResetReason.trim()) {
      setError('Please provide a reason for requiring password reset');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await roleManagementService.requirePasswordReset(
        user.id,
        passwordResetReason
      );

      setSuccess('Password reset requirement set successfully');
      setRequirePasswordReset(true);
      onUpdate && onUpdate();
    } catch (error) {
      setError('Failed to require password reset: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async () => {
    if (selectedRole === user.role) {
      setError('Please select a different role');
      return;
    }

    if (!roleChangeReason.trim()) {
      setError('Please provide a reason for role change');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await roleManagementService.changeUserRole(
        user.id,
        selectedRole,
        roleChangeReason
      );

      setSuccess('User role updated successfully');
      onUpdate && onUpdate();
    } catch (error) {
      setError('Failed to update role: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCohortAssignment = async () => {
    setLoading(true);
    setError('');

    try {
      await roleManagementService.assignCohortsToUser(
        user.id,
        assignedCohorts
      );

      setSuccess('Cohort assignments updated successfully');
      onUpdate && onUpdate();
    } catch (error) {
      setError('Failed to update cohort assignments: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleCohortAssignment = (cohortId) => {
    setAssignedCohorts(prev => {
      if (prev.includes(cohortId)) {
        return prev.filter(id => id !== cohortId);
      } else {
        return [...prev, cohortId];
      }
    });
  };

  const getRoleDisplayInfo = (roleName) => {
    const role = ROLES[roleName?.toUpperCase()];
    return role || { displayName: roleName, description: 'Unknown role' };
  };

  const canManageRole = () => {
    return userProfile?.role === 'admin';
  };

  const canManageCohorts = () => {
    return userProfile?.role === 'admin' &&
           (selectedRole === 'facilitator' || selectedRole === 'assessor');
  };

  if (!user) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Enhanced Security Settings: {user.displayName}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Accordion defaultActiveKey="0">
          <Accordion.Item eventKey="0">
            <Accordion.Header>User Verification Status</Accordion.Header>
            <Accordion.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Verification Status</Form.Label>
                    <div>
                      <Form.Check
                        type="switch"
                        id="verification-switch"
                        label={isVerified ? 'Verified' : 'Unverified'}
                        checked={isVerified}
                        onChange={(e) => setIsVerified(e.target.checked)}
                        disabled={userProfile?.role !== 'admin'}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <strong>Current Status:</strong>
                    <Badge
                      bg={user.isVerified ? 'success' : 'warning'}
                      className="ms-2"
                    >
                      {user.isVerified ? 'Verified' : 'Pending Verification'}
                    </Badge>
                  </div>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Verification Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add notes about verification status..."
                  disabled={userProfile?.role !== 'admin'}
                />
              </Form.Group>

              {userProfile?.role === 'admin' && (
                <Button
                  variant="primary"
                  onClick={handleVerificationUpdate}
                  disabled={loading}
                >
                  Update Verification Status
                </Button>
              )}
            </Accordion.Body>
          </Accordion.Item>

          {canManageRole() && (
            <Accordion.Item eventKey="1">
              <Accordion.Header>Role Management</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Role</Form.Label>
                      <div>
                        <Badge bg={getRoleDisplayInfo(user.role).level === 4 ? 'danger' :
                                   getRoleDisplayInfo(user.role).level === 3 ? 'warning' :
                                   getRoleDisplayInfo(user.role).level === 2 ? 'info' : 'primary'}>
                          {getRoleDisplayInfo(user.role).displayName}
                        </Badge>
                      </div>
                      <small className="text-muted">
                        {getRoleDisplayInfo(user.role).description}
                      </small>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Role</Form.Label>
                      <Form.Select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                      >
                        {Object.values(ROLES).map(role => (
                          <option key={role.name} value={role.name}>
                            {role.displayName}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Reason for Role Change</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={roleChangeReason}
                    onChange={(e) => setRoleChangeReason(e.target.value)}
                    placeholder="Explain why this role change is necessary..."
                  />
                </Form.Group>

                <Button
                  variant="warning"
                  onClick={handleRoleChange}
                  disabled={loading || selectedRole === user.role}
                >
                  Update User Role
                </Button>
              </Accordion.Body>
            </Accordion.Item>
          )}

          {canManageCohorts() && (
            <Accordion.Item eventKey="2">
              <Accordion.Header>Cohort Assignments</Accordion.Header>
              <Accordion.Body>
                <div className="mb-3">
                  <Form.Label>Assigned Cohorts</Form.Label>
                  <div className="mb-2">
                    {user.assignedCohorts?.length > 0 ? (
                      user.assignedCohorts.map(cohortId => {
                        const cohort = availableCohorts.find(c => c.id === cohortId);
                        return (
                          <Badge key={cohortId} bg="info" className="me-2">
                            {cohort?.name || cohortId}
                          </Badge>
                        );
                      })
                    ) : (
                      <small className="text-muted">No cohorts assigned</small>
                    )}
                  </div>
                </div>

                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Assign</th>
                      <th>Cohort Name</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableCohorts.map(cohort => (
                      <tr key={cohort.id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={assignedCohorts.includes(cohort.id)}
                            onChange={() => toggleCohortAssignment(cohort.id)}
                          />
                        </td>
                        <td>{cohort.name}</td>
                        <td>
                          <Badge bg={assignedCohorts.includes(cohort.id) ? 'success' : 'secondary'}>
                            {assignedCohorts.includes(cohort.id) ? 'Assigned' : 'Not Assigned'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Button
                  variant="primary"
                  onClick={handleCohortAssignment}
                  disabled={loading}
                >
                  Update Cohort Assignments
                </Button>
              </Accordion.Body>
            </Accordion.Item>
          )}

          {userProfile?.role === 'admin' && (
            <Accordion.Item eventKey="3">
              <Accordion.Header>Password Security</Accordion.Header>
              <Accordion.Body>
                <div className="mb-3">
                  <strong>Password Reset Status:</strong>
                  <Badge
                    bg={user.requiresPasswordReset ? 'warning' : 'success'}
                    className="ms-2"
                  >
                    {user.requiresPasswordReset ? 'Reset Required' : 'Current'}
                  </Badge>
                </div>

                {!requirePasswordReset && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Reason for Password Reset</Form.Label>
                      <Form.Select
                        value={passwordResetReason}
                        onChange={(e) => setPasswordResetReason(e.target.value)}
                      >
                        <option value="">Select reason...</option>
                        <option value="Security concern">Security concern</option>
                        <option value="Policy compliance">Policy compliance</option>
                        <option value="Account compromise suspected">Account compromise suspected</option>
                        <option value="User requested">User requested</option>
                        <option value="Administrative requirement">Administrative requirement</option>
                      </Form.Select>
                    </Form.Group>

                    <Button
                      variant="warning"
                      onClick={handlePasswordResetRequirement}
                      disabled={loading || !passwordResetReason}
                    >
                      Require Password Reset
                    </Button>
                  </>
                )}

                {user.requiresPasswordReset && (
                  <Alert variant="info">
                    <strong>Password reset is already required for this user.</strong>
                    <br />
                    Reason: {user.passwordResetReason}
                    <br />
                    Required by: {user.passwordResetRequiredBy}
                    <br />
                    Date: {user.passwordResetRequiredAt}
                  </Alert>
                )}
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EnhancedUserSecurity;